import { toast } from 'sonner'
import {
  TAB_BLOCK_IDEAL,
  TAB_BLOCK_WARN,
  TAB_BLOCK_MAX,
  TAB_BLOCK_LIMIT_TOAST,
} from './tab-block-limits'

/** One toast per tab per threshold (no inline banners). */
const shownByTab = new Map()

function markShown(tabId, level) {
  let levels = shownByTab.get(tabId)
  if (!levels) {
    levels = new Set()
    shownByTab.set(tabId, levels)
  }
  levels.add(level)
}

function wasShown(tabId, level) {
  return shownByTab.get(tabId)?.has(level) ?? false
}

/**
 * On notebook session entry — show only the highest tier already reached (one toast).
 * Avoids firing 8/10/12 toasts when opening with prevCount=0 and count=12.
 */
export function notifyTabBlockSessionEntry(tabId, count) {
  if (!tabId || count < TAB_BLOCK_IDEAL) return

  if (count >= TAB_BLOCK_MAX) {
    if (wasShown(tabId, 'max')) return
    markShown(tabId, 'max')
    markShown(tabId, 'warn')
    markShown(tabId, 'nudge')
    toast.message(TAB_BLOCK_LIMIT_TOAST, {
      id: `tab-limit-max-${tabId}`,
      duration: 5500,
    })
    return
  }

  if (count >= TAB_BLOCK_WARN) {
    if (wasShown(tabId, 'warn')) return
    markShown(tabId, 'warn')
    markShown(tabId, 'nudge')
    toast.warning(
      `You're nearing the ${TAB_BLOCK_MAX}-block limit. Add a new tab to stay organized.`,
      {
        id: `tab-limit-warn-${tabId}`,
        duration: 5000,
      },
    )
    return
  }

  if (count >= TAB_BLOCK_IDEAL && !wasShown(tabId, 'nudge')) {
    markShown(tabId, 'nudge')
    toast.info('This tab works best with about 8 focused blocks.', {
      id: `tab-limit-nudge-${tabId}`,
      duration: 4500,
    })
  }
}

/**
 * Fire once when block count crosses 8 / 10 / 12 during an active session.
 */
export function notifyTabBlockThresholdCrossed(tabId, count, prevCount) {
  if (!tabId || count <= prevCount) return

  if (
    count >= TAB_BLOCK_IDEAL &&
    prevCount < TAB_BLOCK_IDEAL &&
    !wasShown(tabId, 'nudge')
  ) {
    markShown(tabId, 'nudge')
    toast.info('This tab works best with about 8 focused blocks.', {
      id: `tab-limit-nudge-${tabId}`,
      duration: 4500,
    })
  }

  if (
    count >= TAB_BLOCK_WARN &&
    prevCount < TAB_BLOCK_WARN &&
    !wasShown(tabId, 'warn')
  ) {
    markShown(tabId, 'warn')
    toast.warning(
      `You're nearing the ${TAB_BLOCK_MAX}-block limit. Add a new tab to stay organized.`,
      {
        id: `tab-limit-warn-${tabId}`,
        duration: 5000,
      },
    )
  }

  if (
    count >= TAB_BLOCK_MAX &&
    prevCount < TAB_BLOCK_MAX &&
    !wasShown(tabId, 'max')
  ) {
    markShown(tabId, 'max')
    toast.message(TAB_BLOCK_LIMIT_TOAST, {
      id: `tab-limit-max-${tabId}`,
      duration: 5500,
    })
  }
}

export function notifyTabBlockAddBlocked() {
  toast.error(TAB_BLOCK_LIMIT_TOAST, { id: 'notebook-tab-block-cap' })
}
